import axios from 'axios';

import { apiBaseUrl } from '../config';
import type { ImageUrls } from '../types/media';

type PresignedUploadResponse = {
  uploadUrl: string;
  // Signed headers must be forwarded unchanged in the S3 request.
  uploadHeaders: Record<string, string>;
  // Only image uploads return public asset URLs.
  urls?: ImageUrls;
};

async function uploadWithPresignedUrl(
  filename: string,
  contentType: string,
  content: File | string,
): Promise<PresignedUploadResponse> {
  // The API authorizes the upload; the content goes directly to S3.
  const { data } = await axios.post<PresignedUploadResponse>(
    `${apiBaseUrl}/upload`,
    { filename, contentType },
  );

  await axios.put(data.uploadUrl, content, {
    headers: data.uploadHeaders,
  });

  return data;
}

export async function uploadImage(
  filename: string,
  file: File,
): Promise<ImageUrls> {
  const { urls } = await uploadWithPresignedUrl(filename, file.type, file);
  if (!urls) throw new Error('Image upload did not return public URLs');
  return urls;
}

export async function uploadJson(
  filename: string,
  value: Record<string, unknown>,
): Promise<void> {
  await uploadWithPresignedUrl(
    filename,
    'application/json',
    JSON.stringify(value),
  );
}

export function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === filename.length - 1) return 'jpg';
  return filename.slice(dotIndex + 1);
}
